<template>
  <div class="setting-card" :style="{ '--accent': '#4F7EB3' }">
    <div class="setting-card-head">
      <span class="sc-accent"><i class="bi bi-geo-alt"></i></span>
      <div class="sc-heading">
        <h2 class="sc-title">{{ $t('calendar.location.title') }}</h2>
        <p class="sc-desc">{{ $t('calendar.location.desc') }}</p>
      </div>
    </div>

    <div class="setting-card-body">
      <div v-if="loadError" class="lc-alert">{{ loadError }}</div>
      <template v-else-if="form">
        <div v-if="needsRegion" class="lc-alert">
          <i class="bi bi-info-circle"></i> {{ $t('calendar.location.needsRegion') }}
        </div>
        <location-calendar-fields
          v-model="form"
          :country-code="countryCode"
          :disabled="isSaving"
          id-prefix="set-loccal"
          @valid="isValid = $event"
        />
        <p class="lc-status">
          <span v-if="store.current.holidays_stored">
            {{ $t('calendar.location.holidaysStored', { n: store.current.holidays_stored }) }}<span v-if="fetchedAt">{{ $t('calendar.location.updated', { date: fetchedAt }) }}</span>.
          </span>
          <span v-else>{{ $t('calendar.location.noHolidays') }}</span>
          <span v-if="store.current.latitude != null && !store.current.weather_history">
            {{ $t('calendar.location.weatherTonight') }}
          </span>
          <br /><span class="lc-credit">{{ store.current.attribution }}</span>
        </p>
      </template>
    </div>

    <div v-if="form" class="form-actions">
      <div v-if="isSaving" class="saving"><div class="saver"></div><span>{{ $t('calendar.location.saving') }}</span></div>
      <button class="btn-solid ok" :disabled="!isChanged || !isValid || isSaving" @click="save">
        <i class="bi bi-save"></i> {{ $t('calendar.location.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { i18n } from '@/i18n'

import { useLocationCalendarStore } from '@/stores/LocationCalendarStore'
import { parseCoordinates } from '@/location/coordinates'
import LocationCalendarFields from '@/components/LocationCalendarFields.vue'

export default {
    name: 'LocationCalendarSettings',

    components: { LocationCalendarFields },

    setup() {
        const store = useLocationCalendarStore()
        const toast = useToast()
        const form = ref(null)
        const original = ref('')
        const isValid = ref(false)
        const isSaving = ref(false)
        const loadError = ref('')

        const toForm = (cur) => ({
            coordinates: cur.latitude != null ? `${cur.latitude}, ${cur.longitude}` : '',
            school_region: cur.calendar.school_region,
            school_region_type: cur.calendar.school_region_type,
            sector: [...(cur.calendar.sector || [])],
        })
        const snapshot = (f) => {
            const c = parseCoordinates(f.coordinates)
            return JSON.stringify([c?.lat, c?.lon, f.school_region, [...(f.sector || [])].sort()])
        }

        const countryCode = computed(() => store.current?.calendar?.country || '')
        const isChanged = computed(() => form.value && snapshot(form.value) !== original.value)
        const needsRegion = computed(() => store.current && !store.current.calendar.school_region
            && (store.regionsByCountry[countryCode.value] || []).length > 0)
        const fetchedAt = computed(() => {
            const iso = store.current?.holidays_fetched_at
            return iso ? new Date(iso).toLocaleDateString() : ''
        })

        async function load() {
            try {
                const cur = await store.fetchCurrent()
                form.value = toForm(cur)
                original.value = snapshot(form.value)
            } catch (err) {
                loadError.value = i18n.global.t('calendar.location.loadFailed')
            }
        }

        async function save() {
            const c = parseCoordinates(form.value.coordinates)
            if (!c) return
            isSaving.value = true
            try {
                const cur = await store.save({
                    latitude: c.lat,
                    longitude: c.lon,
                    country: countryCode.value,
                    school_region: form.value.school_region,
                    school_region_type: form.value.school_region_type,
                    sector: form.value.sector,
                })
                form.value = toForm(cur)
                original.value = snapshot(form.value)
                toast.success(i18n.global.t('calendar.location.saved'))
            } catch (err) {
                toast.error(err.message)
            } finally {
                isSaving.value = false
            }
        }

        onMounted(load)

        return { store, form, isValid, isSaving, isChanged, loadError, countryCode, needsRegion, fetchedAt, save }
    },
}
</script>

<style scoped>
.setting-card { background: #fff; border: 1px solid #e6edf3; border-radius: 12px; box-shadow: 0 1px 2px rgba(13, 38, 76, 0.04); }
.setting-card-head { display: flex; align-items: center; gap: 10px; padding: 13px 16px 12px; border-bottom: 1px solid #f0f3f7; }
.sc-accent { flex: 0 0 auto; display: inline-flex; align-items: center; font-size: 1.05rem; color: var(--accent, #4F7EB3); }
.sc-heading { min-width: 0; }
.sc-title { margin: 0; font-weight: 700; font-size: 0.98rem; color: #2b3a45; }
.sc-desc { margin: 1px 0 0; color: #7c8894; font-size: 0.8rem; line-height: 1.35; }
.setting-card-body { padding: 14px 16px; }
.lc-alert { background: #f2f8fc; color: #425461; border-left: 3px solid #4F7EB3; padding: 8px 12px; border-radius: 6px; margin-bottom: 12px; font-size: .88rem; }
.lc-status { font-size: .8rem; color: #7c8894; margin: 0; }
.lc-credit { font-size: .75rem; color: #9aa6b1; }
.form-actions { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-top: 1px solid #eef3f7; justify-content: flex-end; }
.saving { display: flex; align-items: center; gap: 8px; color: #6b7a86; margin-right: auto; }
.saver { width: 20px; aspect-ratio: 1; border-radius: 50%; background: #B2C149;
  --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m); mask: var(--_m); -webkit-mask-composite: source-out; mask-composite: subtract;
  animation: lc-spin 1s linear infinite; }
@keyframes lc-spin { to { transform: rotate(1turn); } }
.btn-solid { display: inline-flex; align-items: center; gap: 8px; border-radius: 10px; padding: 8px 12px; border: none; color: #fff; cursor: pointer; }
.btn-solid.ok { background: #6C9046; }
.btn-solid:disabled { background: #c9cfd6 !important; opacity: .5; cursor: not-allowed; }
</style>
